import React, { useState, useRef, useEffect } from "react";
import { SavedVideo } from "../video.model";
import { cloudVideoStorage } from "../utils/cloudVideoStorage";

const LiveStream: React.FC = () => {
  const [isStreaming, setIsStreaming] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [viewerCount, setViewerCount] = useState(0);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const recordingStartTimeRef = useRef<number>(0);

  const startStream = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
      }

      setIsStreaming(true);
      setViewerCount(Math.floor(Math.random() * 50) + 1);
    } catch (error) {
      console.error("Error accessing camera/microphone:", error);
      alert("Unable to access camera/microphone. Please check permissions.");
    }
  };

  const stopStream = () => {
    if (isRecording) {
      stopRecording();
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    setIsStreaming(false);
    setViewerCount(0);
  };

  const startRecording = () => {
    if (!streamRef.current) return;

    try {
      const mediaRecorder = new MediaRecorder(streamRef.current, {
        mimeType: "video/webm;codecs=vp9",
      });

      recordedChunksRef.current = [];
      recordingStartTimeRef.current = Date.now();

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        saveRecording();
      };

      mediaRecorder.start(1000);
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
      setRecordingDuration(0);
    } catch (error) {
      console.error("Error starting recording:", error);
      alert("Recording not supported on this device/browser");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const saveRecording = async () => {
    try {
      const blob = new Blob(recordedChunksRef.current, { type: "video/webm" });
      const duration = Math.floor(
        (Date.now() - recordingStartTimeRef.current) / 1000,
      );
      const videoId = Date.now().toString();

      const video: SavedVideo = {
        id: videoId,
        name: `Stream Recording ${new Date().toLocaleDateString()}`,
        description: "Live stream recording",
        duration,
        recordedAt: new Date(),
        blob,
        url: "",
        r2Key: `videos/${videoId}.webm`,
      };

      await cloudVideoStorage.saveVideo(video);
      setRecordingDuration(0);
      alert(`Recording saved! Duration: ${duration} seconds`);
    } catch (error) {
      console.error("Error saving video:", error);
      alert("Failed to save recording. Please try again.");
    }
  };

  const simulateViewerStream = () => {
    setIsViewing(true);
    setViewerCount(Math.floor(Math.random() * 100) + 10);
  };

  const stopViewing = () => {
    setIsViewing(false);
  };

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isStreaming) {
      interval = setInterval(() => {
        setViewerCount((prev) => prev + Math.floor(Math.random() * 3) - 1);
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [isStreaming]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isRecording) {
      interval = setInterval(() => {
        setRecordingDuration(
          Math.floor((Date.now() - recordingStartTimeRef.current) / 1000),
        );
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isRecording]);

  return (
    <div style={{ padding: "2rem" }}>
      <h1>Live Stream</h1>

      <div style={{ marginBottom: "2rem" }}>
        <h2>Broadcaster</h2>
        <div style={{ marginBottom: "1rem" }}>
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            style={{
              width: "100%",
              maxWidth: "640px",
              height: "auto",
              backgroundColor: "#000",
              border: "2px solid #ccc",
            }}
          />
        </div>

        <div style={{ marginBottom: "1rem", display: "flex", gap: "10px" }}>
          {!isStreaming ? (
            <button
              onClick={startStream}
              style={{
                padding: "10px 20px",
                backgroundColor: "#28a745",
                color: "white",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
                fontSize: "16px",
              }}
            >
              Start Streaming
            </button>
          ) : (
            <button
              onClick={stopStream}
              style={{
                padding: "10px 20px",
                backgroundColor: "#dc3545",
                color: "white",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
                fontSize: "16px",
              }}
            >
              Stop Streaming
            </button>
          )}

          {isStreaming && !isRecording && (
            <button
              onClick={startRecording}
              style={{
                padding: "10px 20px",
                backgroundColor: "#fd7e14",
                color: "white",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
                fontSize: "16px",
              }}
            >
              🔴 Start Recording
            </button>
          )}

          {isRecording && (
            <button
              onClick={stopRecording}
              style={{
                padding: "10px 20px",
                backgroundColor: "#6c757d",
                color: "white",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
                fontSize: "16px",
              }}
            >
              ⏹️ Stop Recording
            </button>
          )}
        </div>

        {isStreaming && (
          <div
            style={{
              padding: "10px",
              backgroundColor: "#f8f9fa",
              border: "1px solid #dee2e6",
              borderRadius: "5px",
            }}
          >
            <strong>🔴 LIVE</strong> - {viewerCount} viewers watching
            {isRecording && (
              <div style={{ marginTop: "5px", color: "#fd7e14" }}>
                <strong>🔴 RECORDING</strong> -{" "}
                {Math.floor(recordingDuration / 60)}:
                {(recordingDuration % 60).toString().padStart(2, "0")}
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ borderTop: "1px solid #ccc", paddingTop: "2rem" }}>
        <h2>Viewer Mode</h2>
        <p>Simulate watching a live stream from another broadcaster</p>

        <div style={{ marginBottom: "1rem" }}>
          <div
            style={{
              width: "100%",
              maxWidth: "640px",
              height: "360px",
              backgroundColor: isViewing ? "#333" : "#f0f0f0",
              border: "2px solid #ccc",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: isViewing ? "#fff" : "#666",
              fontSize: "18px",
            }}
          >
            {isViewing ? "📹 Live Stream in Progress..." : "No stream selected"}
          </div>
        </div>

        <div>
          {!isViewing ? (
            <button
              onClick={simulateViewerStream}
              style={{
                padding: "10px 20px",
                backgroundColor: "#007bff",
                color: "white",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
                fontSize: "16px",
              }}
            >
              Join Stream as Viewer
            </button>
          ) : (
            <button
              onClick={stopViewing}
              style={{
                padding: "10px 20px",
                backgroundColor: "#6c757d",
                color: "white",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
                fontSize: "16px",
              }}
            >
              Leave Stream
            </button>
          )}
        </div>

        {isViewing && (
          <div
            style={{
              marginTop: "1rem",
              padding: "10px",
              backgroundColor: "#e7f3ff",
              border: "1px solid #bee5eb",
              borderRadius: "5px",
            }}
          >
            👥 {viewerCount} viewers • 💬 Chat enabled
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveStream;
